import { useEffect, useMemo, useState } from 'react';
import { Box, Link } from '@mui/material';

import TargetSelectRow from './TargetSelectRow';
import { getTargetIDsOfAllGoals } from './utils';

import type { ITreatmentPlanGoal, TargetProgress } from '../../../../../utils/types';

export interface ParentCareGiverGoal {
  id: number;
  goalName: string;
  targets: {
    targetId: number;
    targetLabel: string;
  }[];
}

interface TargetSelectModalContentProps {
  isEditable: boolean;
  targetProgress: TargetProgress[];
  activeCaregiverGoals: ITreatmentPlanGoal[];
  selectedTargetIDs: number[];
  onUpdate: (targetIDs: number[]) => void;
}

function TargetSelectModalContent({
  isEditable,
  targetProgress,
  selectedTargetIDs,
  activeCaregiverGoals,
  onUpdate,
}: TargetSelectModalContentProps) {
  const [savedTargetIDs, setSavedTargetIDs] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const activeTargetIDs = useMemo(
    () =>
      activeCaregiverGoals.flatMap((goal) =>
        goal.targets.flatMap((target) =>
          target.is_active && target.id !== undefined ? [target.id] : [],
        ),
      ),
    [activeCaregiverGoals],
  );

  const goalTargetsData: ParentCareGiverGoal[] = useMemo(() => {
    return activeCaregiverGoals.flatMap((goal: ITreatmentPlanGoal) => {
      if (goal.id === undefined) return [];
      return [
        {
          id: goal.id,
          goalName: goal.goal_name?.label ?? '',
          targets: goal.targets.flatMap((target) =>
            target.is_active && target.id !== undefined
              ? [{ targetId: target.id, targetLabel: target.label }]
              : [],
          ),
        },
      ];
    });
  }, [activeCaregiverGoals]);

  // Update isAllSelected variable if all possible targets are selected
  useEffect(() => {
    const allTargetIDsCount = activeTargetIDs.length;
    const savedTargetIDsCount = getTargetIDsOfAllGoals(targetProgress)?.length ?? 0;

    if (selectedTargetIDs.length + savedTargetIDsCount >= allTargetIDsCount) {
      setIsAllSelected(true);
    } else {
      setIsAllSelected(false);
    }
  }, [activeTargetIDs, targetProgress, selectedTargetIDs]);

  useEffect(() => {
    if (!targetProgress) return;
    const targetIDs = getTargetIDsOfAllGoals(targetProgress);
    setSavedTargetIDs(targetIDs);
  }, [targetProgress]);

  // Add one or more IDs at a time
  const handleAddMultiple = (targetIDs: number[]) => {
    const idsToBeAdded = targetIDs.filter((targetId) => !selectedTargetIDs.includes(targetId));
    onUpdate([...selectedTargetIDs, ...idsToBeAdded]);
  };

  // Remove one or more IDs at a time
  const handleRemoveMultiple = (targetIDs: number[]) => {
    const updatedSelectedIDs = selectedTargetIDs.filter(
      (targetId) => !targetIDs.includes(targetId),
    );
    onUpdate(updatedSelectedIDs);
  };

  // Select or Unselect all targets of all goals
  const handleAddTargetsOfAllGoals = () => {
    if (!isEditable) return;
    if (isAllSelected) {
      onUpdate([]);
      setIsAllSelected(false);
      return;
    }
    const idsToBeAdded = activeTargetIDs.filter((id) => !savedTargetIDs.includes(id));
    onUpdate(idsToBeAdded);
    setIsAllSelected(true);
  };

  return (
    <Box sx={{ height: 'auto', maxHeight: 500, overflow: 'auto', p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Link
          fontSize={14}
          onClick={handleAddTargetsOfAllGoals}
          sx={{ position: 'fixed', mt: -5.5, mr: 4 }}
          data-testid={isAllSelected ? 'Unselect_all' : 'Select_all'}
        >
          {isAllSelected ? 'Unselect all' : 'Select all'}
        </Link>
      </Box>
      {goalTargetsData.map((goal) => (
        <TargetSelectRow
          key={goal.id}
          isEditable={isEditable}
          goal={goal}
          savedTargetIDs={savedTargetIDs}
          selectedTargetIDs={selectedTargetIDs}
          onAdd={handleAddMultiple}
          onRemove={handleRemoveMultiple}
        />
      ))}
    </Box>
  );
}

export default TargetSelectModalContent;
